import {useContext, useEffect, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import AtomInput from '@s-ui/react-atom-input'
import AtomTag, {atomTagDesigns} from '@s-ui/react-atom-tag'
import Context from '@s-ui/react-context'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const SEPARATOR = '::'

const parseUniqueId = uniqueId => {
  const [group, name, id] = uniqueId.split(SEPARATOR)
  return {group, name, id}
}

const generateUniqueId = ({group, name, id}) => [group, name, id].join(SEPARATOR)

const BASE_CLASS = 'mt-FilterBasicInputNumber'

const FilterBasicInputNumber = ({
  allSelectionsId,
  chips = [],
  onChange,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  placeholder,
  suffix,
  suffixIcon,
  value = []
}) => {
  const {i18n, domain} = useContext(Context)
  const {culture: locale} = i18n
  const {track} = useAnalyticsTrack()
  const {TRACKING_EVENT_NAMES} = domain.get('config').get('tracking')

  const [isFocused, setIsFocused] = useState(false)
  const isDirtyRef = useRef(false)
  const {group, name} = allSelectionsId ? parseUniqueId(allSelectionsId) : {}
  const toNamespacedId = val => generateUniqueId({group, name, id: val})

  const committedValue = value[0] === allSelectionsId || value[0] == null ? '' : parseUniqueId(value[0]).id

  const [localValue, setLocalValue] = useState(committedValue)

  useEffect(() => {
    if (!isFocused || !isDirtyRef.current) {
      setLocalValue(committedValue)
      isDirtyRef.current = false
    }
  }, [committedValue, isFocused])

  const handleChange = (ev, {value: inputValue} = {}) => {
    const raw = String(inputValue ?? '').replace(/\D/g, '')
    isDirtyRef.current = true
    setLocalValue(raw)
  }

  const commit = val => {
    onChange(val === '' ? [allSelectionsId] : [toNamespacedId(val)])
  }

  const handleFocus = () => {
    setIsFocused(true)
    onFocusProp?.(name)
  }

  const handleBlur = () => {
    setIsFocused(false)
    isDirtyRef.current = false
    onBlurProp?.()
    if (localValue !== committedValue) commit(localValue)
  }

  const handleKeyDown = ev => {
    if (ev.key === 'Enter') commit(localValue)
  }

  const handleChipClick = chipValue => {
    const val = String(chipValue)
    setLocalValue(val)
    commit(val)
    const filterName = group?.replace('Group', '')
    const chipEventName =
      filterName === 'price' ? TRACKING_EVENT_NAMES.FILTER_PRICE_CHIP_USED : TRACKING_EVENT_NAMES.FILTER_KM_CHIP_USED
    track({event: chipEventName, data: {hit_information: String(chipValue)}})
  }

  return (
    <div className={BASE_CLASS}>
      <AtomInput
        autoComplete="off"
        id={group ? `${group}-${name}` : name}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rightIcon={suffixIcon ?? (suffix ? <span>{suffix}</span> : undefined)}
        value={localValue}
      />
      {chips.length > 0 && (
        <div className={`${BASE_CLASS}-chips${isFocused ? ` ${BASE_CLASS}-chips--visible` : ''}`}>
          {chips.map(chip => (
            <AtomTag
              className={`${BASE_CLASS}-chipsItem`}
              design={atomTagDesigns.OUTLINE}
              key={chip}
              label={`${chip.toLocaleString(locale, {useGrouping: true})}${suffix ? ` ${suffix}` : ''}`}
              onClick={() => handleChipClick(chip)}
              onMouseDown={event => event.preventDefault()}
            />
          ))}
        </div>
      )}
    </div>
  )
}

FilterBasicInputNumber.displayName = 'FilterBasicInputNumber'
FilterBasicInputNumber.propTypes = {
  allSelectionsId: PropTypes.string,
  chips: PropTypes.arrayOf(PropTypes.number),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  suffix: PropTypes.string,
  suffixIcon: PropTypes.node,
  value: PropTypes.array
}

export default FilterBasicInputNumber
